import clsx from 'clsx';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useHasAccounts } from '@shared/hooks/useHasAccounts';

// SVG Icons for bottom nav (matching SideNav style)
const Icons = {
  Dashboard: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  Holdings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  Watchlist: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  News: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Newspaper icon */}
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
      <path d="M18 14h-8" />
      <path d="M15 18h-5" />
      <path d="M10 6h8v4h-8V6Z" />
    </svg>
  ),
};

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

// Paths that require accounts to be enabled
const ACCOUNT_REQUIRED_PATHS = new Set(['/dashboard', '/holdings']);

const navItems: NavItem[] = [
  { path: '/dashboard', icon: <Icons.Dashboard />, label: 'Dashboard' },
  { path: '/holdings', icon: <Icons.Holdings />, label: 'Holdings' },
  { path: '/watchlist', icon: <Icons.Watchlist />, label: 'Watchlists' },
  { path: '/market', icon: <Icons.News />, label: 'Markets' },
];

export function BottomNav() {
  const { hasAccounts } = useHasAccounts();

  const navItemBaseClasses = 'flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 min-w-16 text-text-muted no-underline transition-colors duration-150 [-webkit-tap-highlight-color:transparent] active:opacity-70 group';
  const navIconClasses = 'flex items-center justify-center w-6 h-6';

  return (
    <nav className="fixed bottom-0 left-0 right-0 flex justify-around items-center bg-surface border-t border-border py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] z-[1000] min-h-[var(--bottom-nav-height)]" aria-label="Mobile navigation">
      {navItems.map(({ path, icon, label }) => {
        const disabled = !hasAccounts && ACCOUNT_REQUIRED_PATHS.has(path);

        if (disabled) {
          return (
            <span
              key={path}
              className={clsx(navItemBaseClasses, 'opacity-35 cursor-not-allowed select-none active:opacity-35')}
              title="Add an account to access this feature"
            >
              <span className={clsx(navIconClasses, '[&>svg]:w-6 [&>svg]:h-6')}>{icon}</span>
              <span className="text-xxs font-medium tracking-normal">{label}</span>
            </span>
          );
        }

        return (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) => clsx(
              navItemBaseClasses,
              isActive && 'text-primary'
            )}
          >
            <span className={clsx(navIconClasses, '[&>svg]:w-6 [&>svg]:h-6')}>{icon}</span>
            <span className="text-xxs font-medium tracking-normal">{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default BottomNav;
